import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Widget, PostVoteBehavior } from '../types';
import { submitFeedback } from '../services/mockService';
import { isValidEmail } from '../utils/validation';
import { migrateWidgetConfig } from '../utils/migrateConfig';
import { X, Check, Smile, Meh, Frown, Heart, Angry, ThumbsUp, ThumbsDown } from 'lucide-react';
import Logo from './Logo';
import VoiceInputButton from './VoiceInputButton';
// AudioRecorder is kept as an exported component for the future Whisper-server pipeline.
// VoiceInputButton (Web Speech API, browser-side) is the current default for voice → transcript.

interface WidgetRendererProps {
  widget: Widget;
  previewMode?: boolean; // If true, don't actually save to DB
  onSubmit?: (feedback: {
    response_type: 'positive' | 'negative' | 'emoji_value' | 'email_capture' | 'audio';
    response_value: string | number;
    comment?: string;
    budget?: string;
    email?: string;
    path_taken?: 'positive' | 'negative';
    audio_blob?: Blob;
    is_audio_transcript?: boolean;
  }) => Promise<void>; // Optional SDK submission handler
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  previewMode = false,
  onSubmit,
}) => {
  // Migrate legacy config to new pipeline format
  const migratedWidget = useMemo(
    () => ({ ...widget, config: migrateWidgetConfig(widget.config) }),
    [widget]
  );

  type PostVoteStep = 'feedback' | 'email' | 'message' | 'done';

  const [step, setStep] = useState<
    | 'idle'
    | 'modal'
    | 'email-capture'
    | 'post-vote'
    | 'post-vote-pipeline'
    | 'success'
    | 'dismissed'
  >('idle');
  const [postVoteStep, setPostVoteStep] = useState<PostVoteStep>('done');
  const [activeBehaviors, setActiveBehaviors] = useState<PostVoteBehavior[] | null>(null);
  const [responseValue, setResponseValue] = useState<string>('');
  const [pathTaken, setPathTaken] = useState<'positive' | 'negative' | null>(null);

  // Form States
  const [email, setEmail] = useState('');
  const [budget, setBudget] = useState('<$100');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isFading, setIsFading] = useState(false);
  // Voice transcript flag — set true when the user dictated the comment via VoiceInputButton.
  // Stored on submissions.is_audio_transcript so the dashboard can mark voice-origin comments.
  const [usedVoice, setUsedVoice] = useState(false);

  const primaryColor = migratedWidget.config.color || '#0070FF';

  // Font styles
  const fontClass =
    migratedWidget.config.fontFamily === 'serif'
      ? 'font-serif'
      : migratedWidget.config.fontFamily === 'mono'
        ? 'font-mono'
        : 'font-sans';

  // Helper to read per-path config value based on which path was taken
  const getPathValue = (
    posVal: string | undefined,
    negVal: string | undefined,
    fallback: string
  ) => {
    if (pathTaken === 'negative') return negVal || fallback;
    return posVal || fallback; // positive or null (emoji defaults to positive)
  };

  // Determine the first pipeline step from behaviors array
  const getFirstPipelineStep = (behaviors: PostVoteBehavior[] | undefined): PostVoteStep => {
    if (!behaviors || behaviors.includes('auto-dismiss')) return 'done';
    if (behaviors.includes('detailed-feedback')) return 'feedback';
    if (behaviors.includes('collect-email')) return 'email';
    if (behaviors.includes('custom-message')) return 'message';
    return 'done';
  };

  // Advance to the next pipeline step after completing the current one
  const advancePipelineStep = (
    currentStep: PostVoteStep,
    behaviors: PostVoteBehavior[] | undefined
  ): PostVoteStep => {
    if (!behaviors) return 'done';
    if (currentStep === 'feedback') {
      if (behaviors.includes('collect-email')) return 'email';
      if (behaviors.includes('custom-message')) return 'message';
      return 'done';
    }
    if (currentStep === 'email') {
      if (behaviors.includes('custom-message')) return 'message';
      return 'done';
    }
    return 'done';
  };

  // Start the post-vote pipeline for a path
  const startPipeline = (behaviors: PostVoteBehavior[] | undefined) => {
    setActiveBehaviors(behaviors || null);
    const firstStep = getFirstPipelineStep(behaviors);
    if (firstStep === 'done') {
      // No steps configured — silent auto-dismiss
      if (previewMode) {
        setTimeout(() => setStep('idle'), 1500);
      } else {
        setIsFading(true);
        setTimeout(() => {
          setStep('dismissed');
          setIsFading(false);
        }, 500);
      }
      return;
    }
    setPostVoteStep(firstStep);
    setStep('post-vote-pipeline');
  };

  // Auto-dismiss functionality for success state (ad_space, binary success after full form submit)
  useEffect(() => {
    if (step === 'success' && !previewMode) {
      if (
        migratedWidget.type === 'ad_space' &&
        (migratedWidget.config.postVoteMode ?? 'dismiss') === 'dismiss'
      ) {
        const fadeTimer = setTimeout(() => setIsFading(true), 2500);
        const dismissTimer = setTimeout(() => {
          setStep('dismissed');
          setIsFading(false);
        }, 3000);
        return () => {
          clearTimeout(fadeTimer);
          clearTimeout(dismissTimer);
        };
      }
      const timer = setTimeout(() => {
        setStep('dismissed');
      }, 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step, previewMode, migratedWidget.type, migratedWidget.config.postVoteMode]);

  // Auto-dismiss for pipeline message step
  useEffect(() => {
    if (step === 'post-vote-pipeline' && postVoteStep === 'message') {
      const delay = 1500;
      if (previewMode) {
        const timer = setTimeout(() => setStep('idle'), delay);
        return () => clearTimeout(timer);
      }
      const fadeTimer = setTimeout(() => setIsFading(true), delay - 500);
      const dismissTimer = setTimeout(() => {
        setStep('dismissed');
        setIsFading(false);
      }, delay);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(dismissTimer);
      };
    }
    return undefined;
  }, [step, postVoteStep, previewMode]);

  // Listen for global trigger events (for programmatic control)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOpen = () => {
      setStep('idle');
    };

    const handleClose = () => {
      setStep('dismissed');
    };

    window.addEventListener('verivibe:open', handleOpen);
    window.addEventListener('verivibe:close', handleClose);

    return () => {
      window.removeEventListener('verivibe:open', handleOpen);
      window.removeEventListener('verivibe:close', handleClose);
    };
  }, []);

  // Watermark Component
  const Watermark = () => (
    <div className="flex justify-center mt-4 opacity-50 hover:opacity-100 transition-opacity">
      <a
        href="https://verivibe.app"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 no-underline group cursor-pointer"
      >
        <span className="text-[10px] text-slate-400 font-medium group-hover:text-brand-600 transition-colors">
          Powered by
        </span>
        <Logo
          size={17}
          showText={true}
          textClassName="text-[10px] font-bold text-slate-500 group-hover:text-brand-600 transition-colors"
        />
      </a>
    </div>
  );

  const handleInitialAction = (
    action: 'positive' | 'negative' | 'dismiss' | 'emoji',
    value?: string
  ) => {
    if (action === 'dismiss') {
      setStep('dismissed');
      return;
    }

    if (migratedWidget.type === 'binary') {
      setResponseValue(action === 'positive' ? 'yes' : 'no');
      if (action === 'positive' || action === 'negative') {
        setPathTaken(action);
      }

      // Submit the vote immediately (fire-and-forget for pipeline steps)
      if (!previewMode) {
        const payload = {
          response_type: action as 'positive' | 'negative',
          response_value: action === 'positive' ? 'yes' : 'no',
          path_taken: action as 'positive' | 'negative',
        };
        const submit = onSubmit ? onSubmit(payload) : submitFeedback(migratedWidget.id, payload);
        submit.catch(() => {});
      }

      // Enter the sequential pipeline for the chosen path
      const pathConfig =
        action === 'positive'
          ? migratedWidget.config.positivePostVote
          : migratedWidget.config.negativePostVote;
      startPipeline(pathConfig);
      return;
    } else if (migratedWidget.type === 'emoji' && value) {
      setResponseValue(value);

      // Submit the vote immediately
      if (!previewMode) {
        const payload = {
          response_type: 'emoji_value' as const,
          response_value: value,
        };
        const submit = onSubmit ? onSubmit(payload) : submitFeedback(migratedWidget.id, payload);
        submit.catch(() => {});
      }

      // Emoji uses positivePostVote as the shared config
      startPipeline(migratedWidget.config.positivePostVote);
      return;
    }

    // Ad space goes directly to modal
    setStep('modal');
  };

  // Handle pipeline step submissions (feedback text, email)
  const handlePipelineSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (postVoteStep === 'feedback') {
      // Submit comment as an update
      if (comment && !previewMode) {
        const payload = {
          response_type: (migratedWidget.type === 'emoji'
            ? 'emoji_value'
            : pathTaken === 'positive'
              ? 'positive'
              : 'negative') as 'positive' | 'negative' | 'emoji_value',
          response_value: responseValue,
          comment,
          path_taken: pathTaken || undefined,
          is_audio_transcript: usedVoice || undefined,
        };
        const submit = onSubmit ? onSubmit(payload) : submitFeedback(migratedWidget.id, payload);
        submit.catch(() => {});
      }
      const next = advancePipelineStep('feedback', activeBehaviors || undefined);
      if (next === 'done') {
        if (previewMode) {
          setStep('idle');
        } else {
          setStep('dismissed');
        }
      } else {
        setPostVoteStep(next);
      }
      return;
    }

    if (postVoteStep === 'email') {
      if (email && !isValidEmail(email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (email && !previewMode) {
        const payload = {
          response_type: (migratedWidget.type === 'emoji'
            ? 'emoji_value'
            : pathTaken === 'positive'
              ? 'positive'
              : 'negative') as 'positive' | 'negative' | 'emoji_value' | 'email_capture',
          response_value: responseValue,
          email,
          path_taken: pathTaken || undefined,
        };
        const submit = onSubmit ? onSubmit(payload) : submitFeedback(migratedWidget.id, payload);
        submit.catch(() => {});
      }
      const next = advancePipelineStep('email', activeBehaviors || undefined);
      if (next === 'done') {
        if (previewMode) {
          setStep('idle');
        } else {
          setStep('dismissed');
        }
      } else {
        setPostVoteStep(next);
      }
      return;
    }
  };

  // Handle ad_space / legacy full-form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (email && !isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (previewMode) {
      setStep('success');
      setTimeout(() => setStep('idle'), 2000);
      return;
    }

    setLoading(true);
    setSubmitError(null);
    try {
      const payload: {
        response_type: 'positive' | 'negative' | 'emoji_value' | 'email_capture' | 'audio';
        response_value: string;
        comment?: string;
        budget?: string;
        email?: string;
        path_taken?: 'positive' | 'negative';
        audio_blob?: Blob;
      } = {
        response_type: 'positive',
        response_value: '',
      };

      // audio_blob payload reserved for the transcript pivot (see follow-up).

      if (migratedWidget.type === 'ad_space') {
        payload.response_type = 'email_capture';
        payload.response_value = email;
        payload.budget = budget;
        payload.email = email;
      }

      // Retry with exponential backoff
      const maxRetries = 3;
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          if (onSubmit) {
            await onSubmit(payload);
          } else {
            await submitFeedback(migratedWidget.id, payload);
          }

          setStep('success');
          toast.success('Thanks for your feedback!');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, Math.pow(2, attempt - 1) * 1000));
          }
        }
      }

      const message =
        lastError instanceof Error ? lastError.message : 'Something went wrong. Please try again.';
      setSubmitError(message);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'dismissed') return null;

  // Post-vote pipeline rendering (feedback → email → message → done)
  if (step === 'post-vote-pipeline') {
    // Custom message step — brief message with checkmark then auto-dismiss
    if (postVoteStep === 'message') {
      const customMessage = getPathValue(
        migratedWidget.config.positiveCustomMessage,
        migratedWidget.config.negativeCustomMessage,
        'Thank you!'
      );
      return (
        <div
          className={`flex items-center gap-2 max-w-fit mx-auto animate-in zoom-in duration-300 ${fontClass}`}
          style={{
            opacity: isFading ? 0 : 1,
            transition: 'opacity 500ms ease',
          }}
        >
          <div className="bg-white py-3 pl-4 pr-4 rounded-full shadow-lg border border-slate-100 flex items-center gap-4">
            {migratedWidget.config.profilePictureUrl && (
              <img
                src={migratedWidget.config.profilePictureUrl}
                alt="Profile"
                className="h-10 w-10 rounded-full object-cover border border-slate-100 shadow-sm"
              />
            )}
            <span className="text-slate-800 font-bold text-base">{customMessage}</span>
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
            >
              <Check size={20} strokeWidth={3} />
            </div>
          </div>
        </div>
      );
    }

    // Feedback text step
    if (postVoteStep === 'feedback') {
      const feedbackPlaceholder = getPathValue(
        migratedWidget.config.positiveFeedbackPlaceholder,
        migratedWidget.config.negativeFeedbackPlaceholder,
        migratedWidget.config.placeholder || 'Tell us more...'
      );
      return (
        <div
          className={`bg-white p-5 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl border border-slate-100 w-[320px] sm:w-[380px] mx-auto animate-in slide-in-from-bottom-4 duration-300 ${fontClass}`}
        >
          <div className="flex justify-between items-center mb-4 sm:mb-5">
            <h4 className="font-bold text-slate-800 text-base sm:text-lg">Care to elaborate?</h4>
            <button onClick={() => setStep(previewMode ? 'idle' : 'dismissed')} className="p-1">
              <X size={16} className="text-slate-400 hover:text-slate-600" />
            </button>
          </div>
          <form onSubmit={handlePipelineSubmit}>
            <textarea
              className="w-full p-2.5 sm:p-3 border border-slate-200 rounded-lg sm:rounded-xl mb-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none transition-all text-slate-900"
              rows={3}
              placeholder={feedbackPlaceholder}
              value={comment}
              onChange={e => {
                setComment(e.target.value);
                // If the user manually edits, treat the submission as typed unless they
                // dictate again (the next voice session will re-flag it).
                if (usedVoice) setUsedVoice(false);
              }}
            />
            <div className="mb-3 sm:mb-4">
              <VoiceInputButton
                primaryColor={primaryColor}
                onStart={() => setUsedVoice(true)}
                onTranscript={text => setComment(text)}
              />
            </div>
            <button
              className="w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-white transition-all hover:brightness-110 shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none text-sm sm:text-base"
              style={{ backgroundColor: primaryColor }}
            >
              Send Feedback
            </button>
            {submitError && <p className="text-red-500 text-xs mt-2 text-center">{submitError}</p>}
          </form>
          <Watermark />
        </div>
      );
    }

    // Email capture step
    if (postVoteStep === 'email') {
      const emailHeadline = getPathValue(
        migratedWidget.config.positiveEmailHeadline,
        migratedWidget.config.negativeEmailHeadline,
        "We'd love to follow up!"
      );
      return (
        <div
          className={`bg-white p-5 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl border border-slate-100 w-[320px] sm:w-[380px] mx-auto animate-in slide-in-from-bottom-4 duration-300 ${fontClass}`}
        >
          <div className="flex justify-between items-center mb-4 sm:mb-5">
            <h4 className="font-bold text-slate-800 text-base sm:text-lg">{emailHeadline}</h4>
            <button onClick={() => setStep(previewMode ? 'idle' : 'dismissed')} className="p-1">
              <X size={16} className="text-slate-400 hover:text-slate-600" />
            </button>
          </div>
          <form onSubmit={handlePipelineSubmit} className="space-y-3 sm:space-y-4">
            <div>
              <input
                type="email"
                className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm sm:text-base text-slate-900"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <button
              className="w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-white transition-all hover:brightness-110 shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none text-sm sm:text-base"
              style={{ backgroundColor: primaryColor }}
            >
              Submit
            </button>
            {submitError && <p className="text-red-500 text-xs mt-2 text-center">{submitError}</p>}
          </form>
          <Watermark />
        </div>
      );
    }

    // Fallback: done step should not render (auto-dismiss handles it)
    return null;
  }

  if (step === 'success') {
    if (migratedWidget.type === 'ad_space') {
      const adPostMode = migratedWidget.config.postVoteMode ?? 'dismiss';
      const adMessage =
        adPostMode === 'message'
          ? migratedWidget.config.positiveMessage ||
            migratedWidget.config.thankYouMessage ||
            'Thank you!'
          : migratedWidget.config.thankYouMessage || 'Thank you!';

      return (
        <div
          className={`animate-in fade-in zoom-in duration-300 p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl bg-white border border-brand-100 flex flex-col items-center justify-center text-center w-[320px] sm:w-[380px] mx-auto relative ${fontClass}`}
          style={{
            opacity: isFading ? 0 : 1,
            transition: 'opacity 500ms ease',
          }}
        >
          <button
            onClick={() => setStep('dismissed')}
            className="absolute top-2 right-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
          >
            <X size={16} />
          </button>

          <div className="mb-3 sm:mb-4">
            <Logo size={48} showText={false} className="sm:w-14 sm:h-14" />
          </div>
          <h3 className="text-slate-900 font-bold text-base sm:text-lg mb-1">{adMessage}</h3>
          <Watermark />
        </div>
      );
    }

    // Generic success (shouldn't normally be reached for binary/emoji with new pipeline)
    return (
      <div
        className={`animate-in fade-in zoom-in duration-300 p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl bg-white border border-brand-100 flex flex-col items-center justify-center text-center w-[320px] sm:w-[380px] mx-auto relative ${fontClass}`}
      >
        <button
          onClick={() => setStep('dismissed')}
          className="absolute top-2 right-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
        >
          <X size={16} />
        </button>
        <div className="mb-3 sm:mb-4">
          <Logo size={48} showText={false} className="sm:w-14 sm:h-14" />
        </div>
        <h3 className="text-slate-900 font-bold text-base sm:text-lg mb-1">Feedback Verified</h3>
        <Watermark />
      </div>
    );
  }

  // --- Type A: Ad Space ---
  if (migratedWidget.type === 'ad_space') {
    if (step === 'idle') {
      return (
        <div className="flex items-center gap-2 max-w-fit mx-auto animate-in zoom-in duration-300">
          {migratedWidget.config.showDismiss && (
            <button
              onClick={() => handleInitialAction('dismiss')}
              className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-white/80 hover:bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-100 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 hover:opacity-100"
              title="Dismiss"
            >
              <X size={14} className="sm:w-4 sm:h-4" />
            </button>
          )}

          <div
            className={`flex items-center justify-between p-3 sm:p-4 bg-white border-2 border-dashed rounded-lg sm:rounded-xl shadow-sm min-w-[240px] max-w-fit mx-auto transition-all hover:shadow-lg hover:-translate-y-0.5 ${fontClass} group`}
            style={{ borderColor: primaryColor }}
            onMouseEnter={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '1';
            }}
            onMouseLeave={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '0';
            }}
          >
            <span className="font-medium text-slate-700 mr-3 sm:mr-4 text-sm sm:text-base whitespace-nowrap">
              {migratedWidget.config.question}
            </span>
            <div className="flex gap-2">
              {!migratedWidget.config.showDismiss && (
                <button
                  onClick={() => handleInitialAction('dismiss')}
                  className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <X size={14} className="sm:w-4 sm:h-4" />
                </button>
              )}
              <button
                onClick={() => handleInitialAction('positive')}
                className="h-7 w-7 sm:h-8 sm:w-8 rounded-full text-white flex items-center justify-center transition-all hover:scale-110 shadow-sm active:scale-95"
                style={{ backgroundColor: primaryColor }}
              >
                <Check size={14} className="sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Modal for Ad Space
    return (
      <div
        className={`p-5 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-2xl border border-slate-100 w-[320px] sm:w-[380px] mx-auto animate-in slide-in-from-bottom-2 fade-in duration-300 ${fontClass}`}
      >
        <div className="flex justify-between items-center mb-4 sm:mb-5">
          <h4 className="font-bold text-slate-800 text-base sm:text-lg">Reserve this spot</h4>
          <button onClick={() => setStep('idle')} className="p-1">
            <X size={16} className="sm:w-[18px] sm:h-[18px] text-slate-400 hover:text-slate-600" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-[10px] sm:text-xs font-bold text-slate-500 uppercase mb-1 sm:mb-1.5">
              Email
            </label>
            <input
              required
              type="email"
              className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm sm:text-base text-slate-900"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[10px] sm:text-xs font-bold text-slate-500 uppercase mb-1 sm:mb-1.5">
              Budget
            </label>
            <select
              className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm sm:text-base"
              value={budget}
              onChange={e => setBudget(e.target.value)}
            >
              <option value="<$100">&lt; $100</option>
              <option value="$100-500">$100 - $500</option>
              <option value="$500+">$500+</option>
            </select>
          </div>
          <button
            disabled={loading}
            className="w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-white transition-all hover:brightness-110 shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none disabled:opacity-50 disabled:shadow-none disabled:transform-none text-sm sm:text-base"
            style={{ backgroundColor: primaryColor }}
          >
            {loading ? 'Sending...' : 'Submit Inquiry'}
          </button>
          {submitError && <p className="text-red-500 text-xs mt-2 text-center">{submitError}</p>}
        </form>
        <Watermark />
      </div>
    );
  }

  // --- Type B: Binary ---
  if (migratedWidget.type === 'binary') {
    if (step === 'idle') {
      const isThumbs = migratedWidget.config.binaryIconStyle === 'thumbs';
      const NegativeIcon = isThumbs ? ThumbsDown : X;
      const PositiveIcon = isThumbs ? ThumbsUp : Check;

      return (
        <div className="flex items-center gap-2 max-w-fit mx-auto animate-in zoom-in duration-300">
          {migratedWidget.config.showDismiss && (
            <button
              onClick={() => handleInitialAction('dismiss')}
              className="h-8 w-8 rounded-full bg-white/80 hover:bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-100 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 hover:opacity-100"
              title="Dismiss"
            >
              <X size={16} />
            </button>
          )}

          <div
            className={`bg-white py-3 pl-4 pr-4 rounded-full shadow-lg border border-slate-100 flex items-center gap-4 hover:shadow-xl transition-shadow min-w-[200px] ${fontClass} group`}
            onMouseEnter={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '1';
            }}
            onMouseLeave={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '0';
            }}
          >
            {migratedWidget.config.profilePictureUrl && (
              <img
                src={migratedWidget.config.profilePictureUrl}
                alt="Profile"
                className="h-10 w-10 rounded-full object-cover border border-slate-100 shadow-sm flex-shrink-0"
              />
            )}
            <span className="text-slate-700 font-medium text-base whitespace-nowrap">
              {migratedWidget.config.question}
            </span>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => handleInitialAction('negative')}
                className="h-10 w-10 rounded-full bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              >
                <NegativeIcon size={20} />
              </button>
              <button
                onClick={() => handleInitialAction('positive')}
                className="h-10 w-10 rounded-full text-white flex items-center justify-center transition-all hover:scale-110 shadow-md active:scale-95"
                style={{ backgroundColor: primaryColor }}
              >
                <PositiveIcon size={20} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Binary non-idle steps are handled by the post-vote pipeline above.
    // TODO(transcript-pivot): wire AudioRecorder into the 'detailed-feedback' PostVoteBehavior
    // so users can speak feedback. Audio is uploaded, transcribed (Whisper) by the
    // analyze-feedback edge function, and the transcript is stored as `comment`.
    return null;
  }

  // --- Type C: Emoji ---
  if (migratedWidget.type === 'emoji') {
    const emojis = [
      { icon: Angry, val: 'angry', color: 'text-red-500' },
      { icon: Frown, val: 'sad', color: 'text-orange-500' },
      { icon: Meh, val: 'meh', color: 'text-yellow-500' },
      { icon: Smile, val: 'happy', color: 'text-lime-500' },
      { icon: Heart, val: 'love', color: 'text-pink-500' },
    ];

    if (step === 'idle') {
      return (
        <div className="flex items-center gap-2 max-w-fit mx-auto animate-in zoom-in duration-300">
          {migratedWidget.config.showDismiss && (
            <button
              onClick={() => handleInitialAction('dismiss')}
              className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-white/80 hover:bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-100 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 hover:opacity-100"
              title="Dismiss"
            >
              <X size={14} className="sm:w-4 sm:h-4" />
            </button>
          )}

          <div
            className={`bg-white/95 backdrop-blur-md p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-xl border border-slate-100 min-w-[280px] max-w-fit mx-auto hover:shadow-2xl transition-all ${fontClass} group`}
            onMouseEnter={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '1';
            }}
            onMouseLeave={e => {
              const dismissBtn = e.currentTarget.previousElementSibling as HTMLElement;
              if (dismissBtn) dismissBtn.style.opacity = '0';
            }}
          >
            <p className="text-center text-slate-700 font-semibold mb-4 sm:mb-5 text-base sm:text-lg whitespace-nowrap">
              {migratedWidget.config.question}
            </p>
            <div className="flex justify-center gap-2 sm:gap-4">
              {emojis.map(e => (
                <button
                  key={e.val}
                  onClick={() => handleInitialAction('emoji', e.val)}
                  className="group/emoji flex flex-col items-center gap-1 transition-transform hover:scale-125 active:scale-95"
                >
                  <e.icon
                    className={`h-6 w-6 sm:h-8 sm:w-8 ${e.color} opacity-60 group-hover/emoji:opacity-100 transition-opacity`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Emoji non-idle steps are handled by the post-vote pipeline above.
    // TODO(transcript-pivot): wire AudioRecorder into the 'detailed-feedback' PostVoteBehavior
    // so users can speak feedback. Audio is uploaded, transcribed (Whisper) by the
    // analyze-feedback edge function, and the transcript is stored as `comment`.
    return null;
  }

  return <div>Unknown Widget Type</div>;
};

export default WidgetRenderer;
